import { useMemo } from "react"
import { Link } from "react-router-dom"
import { PLACEHOLDERS } from "assets/images"
import { CopyButton } from "components/Common/CopyButton"
import { generateStockText } from "helpers/stock_helpers"
import formatMileage from "utils/formatMileage"

/**
 * Vehicle model/specs table cell.
 *
 * @param {object}  stock            - Vehicle stock record.
 * @param {boolean} showIdentifiers  - When true, also renders the linked
 *   stock ID and chassis number. Pages that already have a dedicated Stock ID
 *   column (e.g. the stock table) should leave this off to avoid duplication.
 * @param {boolean} showImage        - When true, renders the vehicle photo:
 *   the featured image if one is flagged, otherwise the first image.
 */
const StockModelCell = ({
  stock,
  isMobile,
  toggleRow,
  isExpanded,
  rowId,
  showIdentifiers = false,
  showImage = false,
}) => {
  const expanded = isExpanded(rowId)

  const textToCopy = useMemo(() => generateStockText(stock), [stock])

  // Featured image when flagged, else the first image; backend `primary_image`
  // applies the same rule, and the "photo coming soon" placeholder is the
  // final fallback when the vehicle has no images at all.
  const featuredImage = useMemo(() => {
    const images = Array.isArray(stock?.images) ? stock.images : []
    const primary = images.find(img => img?.isPrimary)
    return (
      primary?.url ||
      images[0]?.url ||
      stock?.primary_image ||
      PLACEHOLDERS.comingSoon
    )
  }, [stock])

  const content = (
    <div
      className="text-wrap"
      style={{
        maxWidth: "500px",
        minWidth: "8rem",
        whiteSpace: "normal",
        overflowWrap: "break-word",
      }}
    >
      <div className="d-flex flex-wrap gap-1">
        <div className="fw-bold">
          {stock.brand?.name ?? "-"} {stock.model?.name ?? "-"}{" "}
          {stock.engine_type?.name ?? "-"}
        </div>

        <div className="d-flex gap-1">
          <CopyButton text={textToCopy} />
          {isMobile && (
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={() => toggleRow(rowId)}
            >
              {expanded ? "▼" : "▶"}
            </button>
          )}
        </div>
      </div>

      {showIdentifiers && (
        <div className="small mt-1">
          <strong className="text-primary">
            <i className="bx bx-hash me-1" />
            <Link to={`/stock-list/${stock.stock_id}/view`}>
              {stock.stock_id ?? "-"}
            </Link>
          </strong>
          {stock.full_chassis ? (
            <>
              {" • "}
              <strong className="text-success">
                <i className="bx bx-barcode me-1" />
                <Link to={`/stock-list/${stock.full_chassis}/view`}>
                  {stock.full_chassis}
                </Link>
              </strong>
            </>
          ) : null}
        </div>
      )}

      {(expanded || !isMobile) && (
        <div className="text-muted d-block mt-1">
          Package: <span className="badge bg-light text-dark">{stock.package?.name ?? "-"}</span>{" "}
          • Year: <span className="badge bg-light text-dark">{stock.year?.name ?? "-"}</span>{" "}
          • Engine CC: <span className="badge bg-light text-dark">{stock.engine_c_c?.name ?? "-"}</span>{" "}
          • Transmission: <span className="badge bg-light text-dark">{stock.transmission?.name ?? "-"}</span>{" "}
          • Fuel: <span className="badge bg-light text-dark">{stock.fuel_type?.name ?? "-"}</span>{" "}
          • Key: <span className="badge bg-light text-dark">{stock.key_type?.name ?? "-"}</span>{" "}
          • Exterior: <span className="badge bg-light text-dark">{stock.exterior_color?.name ?? "-"}</span>{" "}
          • Interior: <span className="badge bg-light text-dark">{stock.interior_color?.name ?? "-"}</span>{" "}
          • Auction Grade: <span className="badge bg-warning">{stock.auction_grade?.name ?? "-"}</span>{" "}
          • Mileage: <span className="badge bg-light text-dark">{formatMileage(stock.mileage)}</span>{" "}
          • Doors: <span className="badge bg-light text-dark">{stock.doors}</span>{" "}
          • Seats: <span className="badge bg-light text-dark">{stock.seats}</span>{" "}
          • Tire: <span className="badge bg-light text-dark">{stock.tire_size ?? "-"}</span>
        </div>
      )}
    </div>
  )

  if (!showImage) {
    return content
  }

  return (
    <div className="d-flex gap-2">
      <img
        src={featuredImage}
        alt="Vehicle"
        className="rounded flex-shrink-0"
        style={{ width: 84, height: 64, objectFit: "cover" }}
        onError={e => {
          e.currentTarget.src = PLACEHOLDERS.comingSoon
        }}
      />
      {content}
    </div>
  )
}

export default StockModelCell
